import { StyledComponent } from "@emotion/styled";
import { ArrowRight } from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import cn from "classnames";
import { Tooltip } from "components-redesign/Tooltip/Tooltip";
import { Empty } from "components/Empty/Empty";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DropdownFilter } from "./DropdownFilter";
import { MultiTabDropdownFilter } from "./MultiTabDropdownFilter";
import DefaultStyles from "./Styles";
import { DatagridColumn } from "./types";
import { useRowSelection } from "./useRowSelection";
import { filterRows, getVisibleRows, sortRows } from "./utils";

interface DatagridProps {
  columns: DatagridColumn[];
  data: any[];
  innerTable?: (row: any) => React.ReactNode;
  onSearch?: (query: string, row: any) => boolean;
  colorizeInner?: boolean;
  pageSize?: number;
  loading?: boolean;
  inner?: boolean;
  Styles?: StyledComponent<any>;
  hasVirtualization?: boolean;
  stickyHeader?: boolean;
  tableContainerStyles?: React.CSSProperties;
  defaultSort?: { field: string; order: "asc" | "desc" };
  rowSelection?: boolean;
  rowSelectionColumnHeader?: string | React.ReactNode;
  selectedRows?: Set<string>;
  onSelectionChange?: (selectedRows: Set<string>) => void;
  defaultAllSelected?: boolean;
  allowSelectAll?: boolean;
  getRowId?: (row: any) => string;
  checkboxExtraProps?: any;
  customRowClassnames?: (row: any) => string | undefined;
  rowTooltip?: (row: any) => string | undefined;
  hideEmptyMessage?: boolean;
  initialColumnFilters?: {
    [field: string]: string[] | string | { [field: string]: string[] };
  };
  onColumnFiltersChange?: (filters: {
    [field: string]: string[] | string | { [field: string]: string[] };
  }) => void;
}

const DEFAULT_SEARCH_FN = () => true;
const DEFAULT_INNER_HEIGHT = 300;
const DEFAULT_ROW_HEIGHT = 62;
const DEFAULT_GET_ROW_ID = (row: any) =>
  row.id || row.key || JSON.stringify(row);

const tableContainerStylesDefault = {
  height: "auto",
};

// browser scroll anchoring nudges scrollTop when rows swap in/out, which flips
// the visible window back and forth forever (React 19 crashes with #185)
const virtualizedContainerStyles = {
  overflowAnchor: "none",
} as const;

export const Datagrid = ({
  onSearch = DEFAULT_SEARCH_FN,
  innerTable,
  columns,
  data,
  colorizeInner,
  pageSize = 50,
  loading,
  inner,
  Styles = DefaultStyles,
  hasVirtualization = false,
  tableContainerStyles,
  stickyHeader = false,
  defaultSort,
  rowSelection = false,
  rowSelectionColumnHeader,
  selectedRows,
  allowSelectAll = false,
  onSelectionChange,
  getRowId = DEFAULT_GET_ROW_ID,
  checkboxExtraProps = {},
  customRowClassnames,
  rowTooltip,
  hideEmptyMessage,
  initialColumnFilters,
  onColumnFiltersChange,
}: DatagridProps) => {
  const guid = useMemo(() => new Date().getTime(), []);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState({});

  useEffect(() => {
    if (initialColumnFilters && Object.keys(initialColumnFilters).length > 0) {
      setColumnFilters(initialColumnFilters as typeof columnFilters);
    }
  }, [initialColumnFilters]);
  const columnMap = useMemo(
    () => new Map(columns.map((column) => [column.field, column])),
    [columns]
  );
  const filteredRows = useMemo(
    () =>
      filterRows({
        data,
        columns,
        columnFilters,
        onSearch,
        globalFilter,
        columnMap,
      }),
    [data, columns, columnFilters, onSearch, globalFilter, columnMap]
  );

  // Use row selection hook
  const {
    selectedRows: selectedRowsLocal,
    handleRowSelectionChange,
    selectionColumn,
  } = useRowSelection({
    rowSelection,
    data,
    loading,
    onSelectionChange,
    selectedRows,
    getRowId,
    checkboxExtraProps,
    header: rowSelectionColumnHeader,
    allowSelectAll,
    visibleRows: filteredRows,
  });

  const [order, setOrder] = useState<any>(defaultSort?.order);
  const [orderBy, setOrderBy] = useState<any>(defaultSort?.field);
  const [openedInners, setOpenedInners] = useState<any>({});
  const scrollStateRef = useRef({ isScrolled: false });
  const tableContainerRef = useRef<HTMLElement | null>(null);
  const stickyColumnWidthsRef = useRef<{ [field: string]: number }>({});
  const rafRef = useRef<number | null>(null);
  const rowHeightCacheRef = useRef<Record<string, number>>({});

  // Combine selection column with provided columns
  const allColumns = useMemo(() => {
    if (!selectionColumn) return columns;
    if (!columns.some((col) => col.sticky))
      return [selectionColumn, ...columns];

    // checkbox has to stick too, otherwise sticky cols slide over it. pinned to its declared width so offsets line up
    const fixedWidthStyle = {
      minWidth: selectionColumn.width,
      maxWidth: selectionColumn.width,
      paddingLeft: 0,
      paddingRight: 0,
    };
    const stickySelectionColumn: DatagridColumn = {
      ...selectionColumn,
      sticky: true,
      cellStyle: fixedWidthStyle,
      headerCellStyle: fixedWidthStyle,
    };
    return [stickySelectionColumn, ...columns];
  }, [selectionColumn, columns]);

  // Sticky columns calculations
  const stickyColumns = useMemo(() => {
    return allColumns.filter((col) => col.sticky);
  }, [allColumns]);

  // Calculate sticky column offsets once after layout
  const stickyColumnOffsets = useMemo(() => {
    const offsets: { [field: string]: number } = {};
    let cumulativeWidth = innerTable ? 48 : 0;

    stickyColumns.forEach((col) => {
      offsets[col.field] = cumulativeWidth;

      const width = typeof col.width === "number" ? col.width : 150;
      cumulativeWidth += width;
    });

    return offsets;
  }, [stickyColumns, innerTable]);

  useLayoutEffect(() => {
    const tableContainer = document.querySelector(`#table-container-${guid}`);
    if (!tableContainer || stickyColumns.length === 0) return;

    tableContainerRef.current = tableContainer as HTMLElement;

    // Measure column widths once after mount
    stickyColumns.forEach((col) => {
      const headerCell = tableContainer.querySelector(
        `[data-field="${col.field}"]`
      );
      if (headerCell) {
        stickyColumnWidthsRef.current[col.field] = (
          headerCell as HTMLElement
        ).offsetWidth;
      }
    });

    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const scrollLeft = tableContainer.scrollLeft;
        const isScrolled = scrollLeft > 0;
        const hasRightOverflow =
          scrollLeft + tableContainer.clientWidth <
          tableContainer.scrollWidth - 1;

        scrollStateRef.current.isScrolled = isScrolled;

        const stickyHeaders = tableContainer.querySelectorAll(
          ".sticky-column-header"
        );
        const stickyCells = tableContainer.querySelectorAll(".sticky-column");

        stickyHeaders.forEach((el) => {
          if (isScrolled) {
            el.classList.add("stuck");
          } else {
            el.classList.remove("stuck");
          }
        });

        stickyCells.forEach((el) => {
          if (isScrolled) {
            el.classList.add("stuck");
          } else {
            el.classList.remove("stuck");
          }
        });

        const rightHeaders = tableContainer.querySelectorAll(
          ".sticky-column-right-header"
        );
        const rightCells = tableContainer.querySelectorAll(
          ".sticky-column-right"
        );

        rightHeaders.forEach((el) =>
          el.classList.toggle("stuck-right", hasRightOverflow)
        );
        rightCells.forEach((el) =>
          el.classList.toggle("stuck-right", hasRightOverflow)
        );
      });
    };

    tableContainer.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      tableContainer.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [guid, stickyColumns]);

  // Virtualize
  const [visibleRows, setVisibleRows] = useState<any>([]);
  const [virtualMargins, setVirtualMargins] = useState<{
    top: number;
    bottom: number;
  }>({ top: 0, bottom: 0 });

  // Non-virtualized mode
  React.useEffect(() => {
    if (hasVirtualization) return;
    setVisibleRows(data);
  }, [data, hasVirtualization]);

  const handleRequestSort = (property: any) => {
    const isDesc = orderBy === property && order === "desc";
    const isAsc = orderBy === property && order === "asc";
    const nextOrder = isAsc ? "desc" : isDesc ? null : "asc";
    setOrder(nextOrder);
    setOrderBy(nextOrder ? property : null);
  };

  const handleGlobalFilterChange = (event: any) => {
    setGlobalFilter(event.target.value);
  };

  const handleColumnFilterChange = (field: any, value: any) => {
    setColumnFilters((prevFilters) => {
      const next = { ...prevFilters, [field]: value };
      onColumnFiltersChange?.(next);
      return next;
    });
  };

  const sortedRows = useMemo(
    () =>
      sortRows({
        rows: filteredRows,
        orderBy,
        order,
        allColumns,
      }),
    [filteredRows, orderBy, order, allColumns]
  );

  // const pagedRows = sortedRows.slice(page * _pageSize, (page + 1) * _pageSize);

  // Virtualization calculation function
  const visibleRowsRef = useRef(visibleRows);
  visibleRowsRef.current = visibleRows;

  const virtualMarginsRef = useRef(virtualMargins);
  virtualMarginsRef.current = virtualMargins;

  const calculateVisibleRows = React.useCallback(() => {
    const tableContainer = document.querySelector(`#table-container-${guid}`);
    if (!tableContainer) return;

    const cache = rowHeightCacheRef.current;

    Array.from(
      tableContainer?.querySelectorAll(`& > table > tbody > tr[data-row-id]`) ||
        []
    ).forEach((tr: any) => {
      const rowId = tr.dataset.rowId;
      if (rowId) {
        cache[rowId] = tr.clientHeight;
      }
    });

    const innerTrHeights = sortedRows.map((row) => {
      const rowId = getRowId(row);
      const cachedHeight = cache[rowId];
      return cachedHeight || DEFAULT_ROW_HEIGHT;
    });

    const tableContainerHeight = tableContainer?.clientHeight || 0;
    const scrollTop = tableContainer?.scrollTop || 0;

    const { visible, marginTop, marginBottom } = getVisibleRows(
      innerTrHeights,
      scrollTop,
      tableContainerHeight
    );

    // Only update state if values actually changed
    const currentVisible = visibleRowsRef.current;
    const currentMargins = virtualMarginsRef.current;

    const visibleChanged =
      visible.length !== currentVisible.length ||
      visible.some((v, i) => v !== currentVisible[i]);

    const marginsChanged =
      marginTop !== currentMargins.top ||
      marginBottom !== currentMargins.bottom;

    if (visibleChanged) {
      setVisibleRows(visible);
    }
    if (marginsChanged) {
      setVirtualMargins({ top: marginTop, bottom: marginBottom });
    }
  }, [guid, sortedRows, getRowId]);

  // Virtualization: immediate calculation + scroll listener + interval for dynamic heights
  useEffect(() => {
    if (!hasVirtualization) return;
    if (inner) return;

    // Calculate immediately on mount and data changes
    calculateVisibleRows();

    const tableContainer = document.querySelector(`#table-container-${guid}`);
    if (!tableContainer) return;

    // Add scroll listener for immediate updates
    const handleScroll = () => {
      calculateVisibleRows();
    };

    tableContainer.addEventListener("scroll", handleScroll, { passive: true });

    // Keep interval for dynamic height updates (expanded rows, etc)
    const intervalId = setInterval(() => {
      calculateVisibleRows();
    }, 100);

    return () => {
      tableContainer.removeEventListener("scroll", handleScroll);
      clearInterval(intervalId);
    };
  }, [hasVirtualization, inner, calculateVisibleRows]);

  return (
    <Styles>
      <Paper
        style={{ "--colorizeInner": colorizeInner ? "1" : "0" } as any}
        elevation={0}>
        {onSearch !== DEFAULT_SEARCH_FN && (
          <TextField
            placeholder="Search..."
            value={globalFilter}
            onChange={handleGlobalFilterChange}
            sx={{ width: "100%" }}
          />
        )}

        <TableContainer
          sx={{
            ...tableContainerStylesDefault,
            ...(hasVirtualization ? virtualizedContainerStyles : {}),
            ...tableContainerStyles,
          }}
          id={`table-container-${guid}`}>
          <Table stickyHeader={stickyHeader}>
            <TableHead
              sx={{
                position: "sticky",
                top: "0px",
                backgroundColor: "#f5f5f5",
              }}>
              <TableRow>
                {!!innerTable && (
                  <TableCell
                    colSpan={0}
                    className={
                      stickyColumns.length > 0 ? "sticky-column-header" : ""
                    }
                    sx={{
                      position: stickyColumns.length > 0 ? "sticky" : undefined,
                      left: stickyColumns.length > 0 ? "0px" : undefined,
                      zIndex: stickyColumns.length > 0 ? 110 : undefined,
                    }}
                  />
                )}
                {allColumns.map(
                  ({
                    field,
                    headerName,
                    renderHeader,
                    align,
                    width,
                    filterable,
                    sortable,
                    filterType,
                    filterOptions,
                    filterGroups,
                    singleGroupSelection,
                    renderFilter,
                    sticky,
                    stickyRight,
                    hideFilterCount,
                    headerCellStyle,
                  }) => {
                    const isSticky = sticky;
                    const stickyLeft = isSticky
                      ? stickyColumnOffsets[field]
                      : undefined;

                    return (
                      <TableCell
                        key={field}
                        align={align || "left"}
                        data-field={field}
                        className={
                          isSticky
                            ? "sticky-column-header"
                            : stickyRight
                              ? "sticky-column-right-header"
                              : ""
                        }
                        sx={{
                          fontWeight: "bold",
                          width,
                          verticalAlign: "top",
                          position:
                            isSticky || stickyRight ? "sticky" : "relative",
                          left: isSticky ? `${stickyLeft}px` : undefined,
                          right: stickyRight ? 0 : undefined,
                          zIndex: isSticky
                            ? 110
                            : stickyRight
                              ? 100
                              : undefined,
                        }}
                        style={headerCellStyle}>
                        <div
                          className="header-container"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-start",
                            justifyContent:
                              align === "center"
                                ? "center"
                                : align === "right"
                                  ? "flex-end"
                                  : "flex-start",
                            gap: "8px",
                            marginLeft: "5px",
                          }}>
                          <div
                            onClick={
                              sortable
                                ? () => handleRequestSort(field)
                                : undefined
                            }
                            style={{
                              cursor: sortable ? "pointer" : "default",
                              position: "relative",
                            }}>
                            {sortable && (
                              <TableSortLabel
                                active={true}
                                direction={orderBy === field ? order : "asc"}
                                onClick={() => handleRequestSort(field)}
                                sx={{
                                  position: "absolute",
                                  left: "-20px",
                                  top: "-1px",
                                  opacity: orderBy === field ? 1 : 0.1,
                                  transition: "opacity 0.2s ease",
                                  "&:hover": {
                                    opacity: 1,
                                    color: "#1D1B20",
                                  },
                                  "& .MuiTableSortLabel-icon": {
                                    fontSize: "14px",
                                    opacity: orderBy === field ? 1 : 0.4,
                                    color:
                                      orderBy === field
                                        ? "#6750A4 !important"
                                        : "#1D1B20 !important",
                                  },
                                  minWidth: "16px",
                                  width: "16px",
                                  height: "16px",
                                  padding: 0,
                                }}>
                                {/* Empty content but needed for icon to show */}
                                <span
                                  style={{ visibility: "hidden", fontSize: 0 }}>
                                  .
                                </span>
                              </TableSortLabel>
                            )}
                            {renderHeader
                              ? renderHeader()
                              : headerName || field}
                          </div>

                          {filterable && (
                            <>
                              {renderFilter ? (
                                renderFilter(
                                  columnFilters[field] ||
                                    (filterType === "dropdown"
                                      ? []
                                      : filterGroups
                                        ? {}
                                        : ""),
                                  (value) =>
                                    handleColumnFilterChange(field, value),
                                  filterOptions
                                )
                              ) : filterGroups ? (
                                <MultiTabDropdownFilter
                                  filterGroups={filterGroups.map((g) => ({
                                    name: g.name,
                                    field: g.field,
                                    options: g.options,
                                  }))}
                                  selectedValues={columnFilters[field] || {}}
                                  onChange={(values) =>
                                    handleColumnFilterChange(field, values)
                                  }
                                  hideFilterCount={hideFilterCount}
                                  singleGroupSelection={singleGroupSelection}
                                />
                              ) : filterType === "dropdown" && filterOptions ? (
                                <DropdownFilter
                                  options={filterOptions}
                                  selectedValues={columnFilters[field] || []}
                                  onChange={(values) =>
                                    handleColumnFilterChange(field, values)
                                  }
                                  hideFilterCount={hideFilterCount}
                                />
                              ) : (
                                <TextField
                                  value={columnFilters[field] || ""}
                                  onChange={(e) =>
                                    handleColumnFilterChange(
                                      field,
                                      e.target.value
                                    )
                                  }
                                  placeholder={`Filter ${headerName || field}`}
                                  variant="standard"
                                  size="small"
                                />
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    );
                  }
                )}
              </TableRow>
            </TableHead>

            {!loading && (
              <>
                <TableBody>
                  {sortedRows.map((row, i) => {
                    if (!inner && !visibleRows?.[i]) return null;

                    const rowId = getRowId(row);
                    const rowKey = `row-${row.key ?? rowId}`;

                    const isFirstShown = i === visibleRows.findIndex(Boolean);
                    const isLastShown =
                      i === visibleRows.findLastIndex(Boolean);

                    const ir = (
                      <TableRow
                        className={cn("inner-table-row")}
                        key={`innerrow-${i}`}
                        data-i={i}>
                        <TableCell
                          key={`innerrowcell-${i}`}
                          colSpan={allColumns.length + (innerTable ? 1 : 0)}>
                          {innerTable?.(row)}
                        </TableCell>
                      </TableRow>
                    );

                    const isRowSelected = rowSelection
                      ? selectedRowsLocal?.has(rowId)
                      : true;

                    const tr = (
                      <TableRow
                        key={rowKey}
                        data-row-id={rowId}
                        className={cn(
                          "table-row",
                          openedInners[rowKey] && "opened",
                          !isRowSelected && "unselected-row",
                          customRowClassnames?.(row)
                        )}
                        style={{
                          transition: "filter 0.2s ease, opacity 0.2s ease",
                        }}>
                        {!!innerTable && (
                          <TableCell
                            key={`inner-toggle-${i}`}
                            className={
                              stickyColumns.length > 0 ? "sticky-column" : ""
                            }
                            sx={{
                              position:
                                stickyColumns.length > 0 ? "sticky" : undefined,
                              left:
                                stickyColumns.length > 0 ? "0px" : undefined,
                              zIndex: stickyColumns.length > 0 ? 10 : undefined,
                            }}>
                            <div
                              className={`datagrid-inner-toggle ${
                                openedInners[rowKey] && "opened"
                              }`}>
                              <ArrowRight
                                sx={{ cursor: "pointer" }}
                                onClick={() =>
                                  setOpenedInners((prev: any) => ({
                                    ...prev,
                                    [rowKey]: !openedInners[rowKey],
                                  }))
                                }
                              />
                            </div>
                          </TableCell>
                        )}

                        {allColumns.map((column) => {
                          const isSticky = column.sticky;
                          const stickyLeft = isSticky
                            ? stickyColumnOffsets[column.field]
                            : undefined;

                          return (
                            <TableCell
                              key={column.field}
                              align={column.align || "left"}
                              className={
                                isSticky
                                  ? "sticky-column"
                                  : column.stickyRight
                                    ? "sticky-column-right"
                                    : ""
                              }
                              sx={{
                                width: column.width,
                                position:
                                  isSticky || column.stickyRight
                                    ? "sticky"
                                    : undefined,
                                left: isSticky ? `${stickyLeft}px` : undefined,
                                right: column.stickyRight ? 0 : undefined,
                                zIndex: isSticky
                                  ? 10
                                  : column.stickyRight
                                    ? 3
                                    : undefined,
                              }}
                              style={column.cellStyle}>
                              {column.renderCell
                                ? column.renderCell(row, row[column.field])
                                : row[column.field]}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );

                    const tooltipText = rowTooltip?.(row);
                    const trWithTooltip = tooltipText ? (
                      <Tooltip
                        title={tooltipText}
                        placement="bottom"
                        enterDelay={0}>
                        {tr}
                      </Tooltip>
                    ) : (
                      tr
                    );

                    return (
                      <>
                        {!inner && isFirstShown && (
                          <TableRow
                            sx={{ height: virtualMargins.top }}></TableRow>
                        )}
                        {trWithTooltip}
                        {!!innerTable && openedInners[rowKey] && ir}
                        {!inner && isLastShown && (
                          <TableRow
                            sx={{ height: virtualMargins.bottom }}></TableRow>
                        )}
                      </>
                    );
                  })}

                  {sortedRows.length === 0 && !hideEmptyMessage && (
                    <TableRow>
                      <TableCell
                        colSpan={allColumns.length + (innerTable ? 1 : 0)}>
                        <Empty message="No data to display" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            )}
            {loading && (
              <TableBody>
                {Array.from(Array(8).keys()).map(() => (
                  <TableRow>
                    <TableCell
                      sx={{ padding: 0, paddingInline: "5px" }}></TableCell>

                    {allColumns.map(() => (
                      <TableCell sx={{ padding: 0, paddingInline: "5px" }}>
                        <Skeleton height={53} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>
    </Styles>
  );
};
